import React from 'react';
import { Users, MessageCircle, Clock, WifiOff, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { Platform } from '../../../types/platform';
import { PageDashboard } from '../../dashboard/PageDashboard';

interface OverviewTabProps {
  account: Platform;
}

const PLATFORM_LABEL: Record<string, string> = {
  facebook: 'Facebook',
  facebook_browser: 'Facebook Personal',
  zalo: 'Zalo OA',
  zalo_browser: 'Zalo Personal',
  instagram: 'Instagram',
};

const PLATFORM_COLOR: Record<string, string> = {
  facebook: 'bg-[#1877F2]/10 text-[#1877F2]',
  facebook_browser: 'bg-indigo-100 text-indigo-600',
  zalo: 'bg-[#0068FF]/10 text-[#0068FF]',
  zalo_browser: 'bg-[#0068FF]/10 text-[#0068FF]',
  instagram: 'bg-[#E1306C]/10 text-[#E1306C]',
};

const StatusBadge = ({ status, isBrowser }: { status: Platform['status']; isBrowser?: boolean }) => {
  const { t } = useTranslation('chatbot');
  const map = isBrowser
    ? {
        active: { label: t('overviewChatbotOn'), icon: <CheckCircle size={12} />, cls: 'text-emerald-600 bg-emerald-50' },
        inactive: { label: t('overviewChatbotOff'), icon: <WifiOff size={12} />, cls: 'text-amber-500 bg-amber-50' },
        error: { label: t('overviewStatusError'), icon: <AlertTriangle size={12} />, cls: 'text-red-600 bg-red-50' },
      }
    : {
        active: { label: t('overviewStatusActive'), icon: <CheckCircle size={12} />, cls: 'text-emerald-600 bg-emerald-50' },
        inactive: { label: t('overviewStatusPaused'), icon: <WifiOff size={12} />, cls: 'text-gray-500 bg-gray-100' },
        error: { label: t('overviewStatusError'), icon: <AlertTriangle size={12} />, cls: 'text-red-600 bg-red-50' },
      };
  const { label, icon, cls } = map[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>
      {icon} {label}
    </span>
  );
};

const TokenBadge = ({ status, isBrowser }: { status: Platform['access_token_status']; isBrowser?: boolean }) => {
  const { t } = useTranslation('chatbot');
  if (isBrowser) return null;
  const map = {
    valid: { label: t('overviewTokenValid'), cls: 'text-emerald-600 bg-emerald-50' },
    expired: { label: t('overviewTokenExpired'), cls: 'text-red-600 bg-red-50' },
    missing: { label: t('overviewTokenNotConfigured'), cls: 'text-amber-600 bg-amber-50' },
  };
  const { label, cls } = map[status];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
};


const StatCard = ({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string | number; sub?: string }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4">
    <div className="flex items-center gap-2 text-gray-400 mb-2">
      {icon}
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-2xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

export const OverviewTab: React.FC<OverviewTabProps> = ({ account }) => {
  const { t } = useTranslation('chatbot');
  const lastActivity = formatDistanceToNow(new Date(account.last_activity), { addSuffix: true, locale: vi });
  const isBrowser = account.platform.endsWith('_browser');

  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-start gap-4">
          <img
            src={account.avatar}
            alt={account.name}
            className="w-16 h-16 rounded-2xl object-cover flex-shrink-0 bg-gray-100"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-xl font-bold text-gray-900 leading-tight">{account.name}</h2>
                <p className="text-sm text-gray-400 mt-0.5 font-mono">{account.platform_id}</p>
              </div>
              <StatusBadge status={account.status} isBrowser={isBrowser} />
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PLATFORM_COLOR[account.platform]}`}>
                {PLATFORM_LABEL[account.platform]}
              </span>
              <TokenBadge status={account.access_token_status} isBrowser={isBrowser} />
            </div>

            {account.description && (
              <p className="text-sm text-gray-600 mt-3 leading-relaxed">{account.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          icon={<Users size={14} />}
          label="Followers"
          value={account.followers}
        />
        <StatCard
          icon={<MessageCircle size={14} />}
          label={t('overviewPendingMessages')}
          value={account.unread_count}
        />
        <StatCard
          icon={<Clock size={14} />}
          label={t('overviewLastActive')}
          value={lastActivity}
          sub={t('overviewLastActiveSub')}
        />
      </div>

      {/* AI Config Summary */}
      {(account.personality || account.tone || account.greeting) && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('overviewAiConfig')}</h3>
          <div className="space-y-2">
            {account.personality && (
              <div className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">{t('overviewPersonality')}</span>
                <span className="text-sm font-medium text-gray-800 capitalize">{account.personality}</span>
              </div>
            )}
            {account.tone && (
              <div className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">{t('overviewTone')}</span>
                <span className="text-sm font-medium text-gray-800 capitalize">{account.tone}</span>
              </div>
            )}
            {account.greeting && (
              <div className="py-2">
                <span className="text-sm text-gray-500">{t('overviewGreeting')}</span>
                <p className="text-sm text-gray-700 mt-1 italic">"{account.greeting}"</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Replies Summary */}
      {account.quickReplies && account.quickReplies.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {t('overviewQuickReplies', { count: account.quickReplies.length })}
          </h3>
          <div className="space-y-2">
            {account.quickReplies.map((qr, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <span className="text-[#3390ec] font-mono bg-blue-50 px-2 py-0.5 rounded shrink-0">{qr.trigger}</span>
                <span className="text-gray-500 truncate">{qr.response}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-page full dashboard */}
      {account.platform_id && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-4">{t('overviewPageStats')}</h3>
          <PageDashboard pageId={account.platform_id} />
        </div>
      )}
    </div>
  );
};
